import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Modal, InjectedModalProps } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import RoiCalculatorModal from 'components/RoiCalculatorModal'
import useTokenBalance from 'hooks/useTokenBalance'
import tokens from 'config/constants/tokens'
import useVvsBarHandlers from 'views/xVvs/hooks/useVvsBarHandlers'
import useToast from 'hooks/useToast'
import xVvsContext from 'views/xVvs/context'
import { useTransactionAdder } from 'state/transactions/hooks'
import useXvvsRate from 'views/xVvs/hooks/useXvvsRate'
import { formatNumber } from 'utils/formatBalance'
import styled from 'styled-components'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import ConfirmStep from './ConfirmStep'
import InputStep from './InputStep'

interface StakeModalProps extends InjectedModalProps {
  isUnstaking: boolean
  customOnDismiss: () => void
  apr: number
}

const StyledModal = styled(Modal)<{ step: number }>`
  > div:first-child {
    ${({ step }) =>
      step === 1
        ? ` padding-left: 55px;
    padding-right: 55px;`
        : ''}
  }
`

const StakeModal: React.FC<StakeModalProps> = ({ onDismiss, customOnDismiss, isUnstaking, apr }) => {
  const { t } = useTranslation()
  const [pendingTx, setPendingTx] = useState<boolean>()
  const [showRoiModal, setShowRoiModal] = useState(false)
  const openRoiModal = useCallback(() => setShowRoiModal(true), [])
  const closeRoiModal = useCallback(() => setShowRoiModal(false), [])

  const { state, goPrev } = useContext(xVvsContext)
  const { step, value } = state

  const token = isUnstaking ? tokens.xvvs : tokens.vvs
  const { balance: vvsBalance } = useTokenBalance(tokens.vvs.address)
  const price = usePriceVvsUsdc()
  const rate = useXvvsRate()

  const { toastError } = useToast()

  const { onEnterPool, onLeavePool } = useVvsBarHandlers()

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss()
  }, [customOnDismiss, onDismiss])

  const title = useMemo(() => {
    if (step === 0) {
      return isUnstaking ? t('Unstake xVVS Tokens') : t('Stake VVS Tokens')
    }
    return isUnstaking ? t('Confirm Unstake xVVS Tokens') : t('Confirm Stake VVS Tokens')
  }, [isUnstaking, step, t])

  const addTransaction = useTransactionAdder()
  const handleConfirmClick = async () => {
    setPendingTx(true)
    try {
      if (isUnstaking) {
        // unstaking
        const response = await onLeavePool(value)
        addTransaction(response, {
          title: t('xVVS Unstaking Transaction Completed'),
          summary: t('%value% %symbol% have been unstaked', {
            value: formatNumber(Number(value), 2, 6),
            symbol: tokens.xvvs.symbol,
          }),
        })
      } else {
        // staking
        const response = await onEnterPool(value)
        addTransaction(response, {
          title: t('VVS Staking Transaction Completed'),
          summary: t('%value% %symbol% have been staked', {
            value: formatNumber(Number(value), 2, 6),
            symbol: tokens.vvs.symbol,
          }),
        })
      }
    } catch (e: any) {
      console.error(e)
      toastError(
        isUnstaking ? t('xVVS Unstaking Transaction Failed') : t('VVS Staking Transaction Failed'),
        isUnstaking ? t('Your xVVS amount has been reverted') : t('Your VVS amount has been reverted'),
      )
    } finally {
      setPendingTx(false)
      handleDismiss()
    }
  }

  if (showRoiModal) {
    return (
      <RoiCalculatorModal
        linkLabel={t('Get VVS')}
        stakingTokenBalance={vvsBalance}
        stakingTokenSymbol={tokens.vvs.symbol}
        stakingTokenPrice={price.toNumber()}
        earningTokenPrice={price.toNumber()}
        earningTokenSymbol={tokens.vvs.symbol}
        apr={apr}
        linkHref={`./swap?inputCurrency=${tokens.usdc.address}&outputCurrency=${tokens.vvs.address}`}
        tooltipText={t('“My Balance” here includes %assetSymbol% in your wallet.', { assetSymbol: tokens.vvs.symbol })}
        onBack={closeRoiModal}
      />
    )
  }

  return (
    <StyledModal
      title={title}
      bodyPadding="4px 24px 0 24px"
      onDismiss={handleDismiss}
      onBack={step === 1 ? goPrev : undefined}
      step={step}
    >
      {step === 0 ? (
        <InputStep
          isUnstaking={isUnstaking}
          rate={rate}
          onDismiss={handleDismiss}
          onCalculatorClick={openRoiModal}
          token={token}
          apr={apr}
        />
      ) : (
        <ConfirmStep
          isUnstaking={isUnstaking}
          inputValue={value}
          rate={rate}
          remark={
            isUnstaking
              ? t(
                  'By converting your xVVS back to VVS, you will stop earning the APR displayed above accrued from the VVS platform buybacks.',
                )
              : t(
                  'You will stake VVS to receive xVVS. xVVS is not tradable and increases value as you will be entitled to more VVS over time from platform buyback.',
                )
          }
          onDismiss={handleDismiss}
          onConfirm={handleConfirmClick}
          onCalculatorClick={openRoiModal}
          earningToken={isUnstaking ? tokens.vvs : tokens.xvvs}
          stakingToken={isUnstaking ? tokens.xvvs : tokens.vvs}
          pendingTx={pendingTx}
          apr={apr}
        />
      )}
    </StyledModal>
  )
}

export default StakeModal
